import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  FileText,
  Loader2,
  MessageSquareText,
  Mic,
  Paperclip,
  Pencil,
  Plus,
  SearchX,
  Trash2,
  TriangleAlert,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'src/@/components/ui/dialog';
import { Input } from 'src/@/components/ui/input';
import useAuth from 'src/hooks/useAuth';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import actionDeletarRespostaRapida from 'src/actions/dashboard/respostas-rapidas/actionDeletarRespostaRapida';
import DrawerFormRespostaRapida from 'src/sections/@dashboard/configuracoes/respostasRapidas/DrawerFormRespostaRapida';
import D from './ModalMensagensRapidas.design';

const TIPOS = [
  { id: 'todos', labelKey: 'quickReplies.all' },
  { id: 'texto', labelKey: 'quickReplies.text' },
  { id: 'arquivo', labelKey: 'quickReplies.file' },
  { id: 'audio', labelKey: 'quickReplies.audio' },
];

function metaDoTipo(tipo) {
  if (tipo === 'arquivo') {
    return { Icon: Paperclip, tileClass: D.typeFile, labelKey: 'quickReplies.file' };
  }
  if (tipo === 'audio') {
    return { Icon: Mic, tileClass: D.typeAudio, labelKey: 'quickReplies.audio' };
  }
  return { Icon: MessageSquareText, tileClass: D.typeText, labelKey: 'quickReplies.text' };
}

function nomeDoArquivo(item) {
  const arquivo = item?.files?.[0];
  return arquivo?.name || arquivo?.filename || arquivo?.originalname || arquivo?.key?.split('/').pop() || '';
}

export default function ModalMensagensRapidas({
  data = [],
  pesquisaRespostaRapida,
  dialogRespostasRapidas,
  onClose,
  changePesquisa,
  onSelect,
  loading = false,
  error = false,
  onRetry,
  onRefresh,
}) {
  const { t } = useTranslation('chat');
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [filtroTipo, setFiltroTipo] = useState('todos');

  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState('criar');
  const [drawerData, setDrawerData] = useState(null);

  const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
  const [idRespostaRapida, setIdRespostaRapida] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const contagens = useMemo(() => {
    const resultado = { todos: data.length, texto: 0, arquivo: 0, audio: 0 };
    data.forEach((item) => {
      if (resultado[item?.tipo] !== undefined) resultado[item.tipo] += 1;
    });
    return resultado;
  }, [data]);

  const mensagensFiltradas = useMemo(() => {
    const termo = (pesquisaRespostaRapida || '').trim().toLocaleLowerCase();
    return data.filter((item) => {
      if (filtroTipo !== 'todos' && item?.tipo !== filtroTipo) return false;
      if (!termo) return true;

      const conteudo = `${item?.titulo || ''} ${item?.resposta || ''} ${nomeDoArquivo(item)}`;
      return conteudo.toLocaleLowerCase().includes(termo);
    });
  }, [data, filtroTipo, pesquisaRespostaRapida]);

  const temFiltros = filtroTipo !== 'todos' || Boolean(pesquisaRespostaRapida?.trim());

  const handleOpenCreate = () => {
    setDrawerMode('criar');
    setDrawerData(null);
    setOpenDrawer(true);
  };

  const handleOpenEdit = (item, e) => {
    e.stopPropagation();
    setDrawerMode('editar');
    setDrawerData(item);
    setOpenDrawer(true);
  };

  const handleRequestDelete = (item, e) => {
    e.stopPropagation();
    setIdRespostaRapida(item._id);
    setOpenModalConfirmacao(true);
  };

  const handleCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
    setDrawerData(null);
  }, []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await actionDeletarRespostaRapida({ id: idRespostaRapida });
      enqueueSnackbar('Resposta rápida excluída.', { variant: 'info' });
      onRefresh?.();
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Não foi possível excluir a resposta rápida.', { variant: 'error' });
    } finally {
      setDeleting(false);
      setOpenModalConfirmacao(false);
      setIdRespostaRapida(null);
    }
  };

  return (
    <>
      <Dialog
        open={dialogRespostasRapidas}
        onOpenChange={(nextOpen) => {
          // Drawer customizado fica fora do Dialog — não fechar o modal por baixo.
          if (!nextOpen && (openDrawer || openModalConfirmacao)) return;
          onClose(nextOpen);
        }}
        modal={!openDrawer}
      >
        <DialogContent
          className={D.content}
          overlayClassName={D.overlay}
          onInteractOutside={(e) => {
            if (openDrawer || openModalConfirmacao) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (openDrawer || openModalConfirmacao) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            if (openDrawer || openModalConfirmacao) e.preventDefault();
          }}
        >
          <DialogHeader className={D.header}>
            <div className={D.headerRow}>
              <div className="min-w-0">
                <DialogTitle className={D.title}>{t('input.quickReplies')}</DialogTitle>
                <DialogDescription className={D.description}>
                  {t('quickReplies.description')}
                </DialogDescription>
              </div>
              <button type="button" onClick={handleOpenCreate} className={D.newButton}>
                <Plus className="h-4 w-4" />
                {t('quickReplies.new')}
              </button>
            </div>
          </DialogHeader>

          <div className={D.controls}>
            <div>
              <label className={D.label} htmlFor="busca-resposta-rapida">
                {t('quickReplies.searchLabel')}
              </label>
              <Input
                id="busca-resposta-rapida"
                value={pesquisaRespostaRapida || ''}
                onChange={changePesquisa}
                placeholder={t('quickReplies.searchPlaceholder')}
                className={D.input}
                autoFocus
              />
            </div>

            <div className={D.filters} aria-label={t('quickReplies.filterLabel')}>
              {TIPOS.map((tipo) => {
                const ativo = filtroTipo === tipo.id;
                return (
                  <button
                    key={tipo.id}
                    type="button"
                    aria-pressed={ativo}
                    onClick={() => setFiltroTipo(tipo.id)}
                    className={`${D.filter} ${ativo ? D.filterActive : D.filterIdle}`}
                  >
                    {t(tipo.labelKey)}
                    <span className={ativo ? D.countActive : D.count}>{contagens[tipo.id]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {!loading && !error && mensagensFiltradas.length > 0 && (
            <div className={D.listHeader}>
              <span>{t('quickReplies.resultCount', { count: mensagensFiltradas.length })}</span>
              <span>{t('quickReplies.selectHint')}</span>
            </div>
          )}

          <div className={D.list}>
            {loading && (
              <div className={D.state}>
                <div className={D.loadingRow}>
                  <Loader2 className="h-4 w-4 animate-spin text-[#7C3AED]" />
                  {t('quickReplies.loading')}
                </div>
              </div>
            )}

            {!loading && error && (
              <div className={D.state}>
                <div className={D.stateIcon}>
                  <TriangleAlert className="h-5 w-5" />
                </div>
                <p className={D.stateTitle}>{t('quickReplies.errorTitle')}</p>
                <p className={D.stateDescription}>{t('quickReplies.errorDescription')}</p>
                <button type="button" className={D.retry} onClick={onRetry}>
                  {t('quickReplies.retry')}
                </button>
              </div>
            )}

            {!loading && !error && mensagensFiltradas.length === 0 && (
              <div className={D.state}>
                <div className={D.stateIcon}>
                  {temFiltros ? <SearchX className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                </div>
                <p className={D.stateTitle}>
                  {t(temFiltros ? 'quickReplies.noResultsTitle' : 'quickReplies.emptyTitle')}
                </p>
                <p className={D.stateDescription}>
                  {t(temFiltros ? 'quickReplies.noResultsDescription' : 'quickReplies.emptyDescription')}
                </p>
                {!temFiltros && (
                  <button type="button" className={D.createFirst} onClick={handleOpenCreate}>
                    {t('quickReplies.createFirst')}
                  </button>
                )}
              </div>
            )}

            {!loading &&
              !error &&
              mensagensFiltradas.map((item, index) => {
                const { Icon, tileClass, labelKey } = metaDoTipo(item?.tipo);
                const preview =
                  item?.tipo === 'texto'
                    ? item?.resposta
                    : nomeDoArquivo(item) ||
                      t(`quickReplies.${item?.tipo === 'audio' ? 'audioPreview' : 'filePreview'}`);

                return (
                  <div
                    key={item?._id || `${item?.titulo || 'resposta'}-${index}`}
                    role="button"
                    tabIndex={0}
                    className={D.item}
                    onClick={() => onSelect(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onSelect(item);
                    }}
                  >
                    <span className={`${D.typeTile} ${tileClass}`}>
                      <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                    </span>
                    <span className={D.itemBody}>
                      <span className={D.itemTitle}>{item?.titulo || t(labelKey)}</span>
                      <span className={D.itemPreview}>{preview || t('quickReplies.noPreview')}</span>
                    </span>
                    <span className={D.itemActions}>
                      <button
                        type="button"
                        title={t('quickReplies.edit')}
                        className={D.actionBtn}
                        onClick={(e) => handleOpenEdit(item, e)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        title={t('quickReplies.delete')}
                        className={D.actionBtnDanger}
                        onClick={(e) => handleRequestDelete(item, e)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <span className={D.useLabel}>{t('quickReplies.use')}</span>
                    </span>
                  </div>
                );
              })}
          </div>
        </DialogContent>
      </Dialog>

      <DrawerFormRespostaRapida
        open={openDrawer}
        onClose={handleCloseDrawer}
        mode={drawerMode}
        initialData={drawerData}
        clienteId={user?.clienteId}
        onSuccess={onRefresh}
        onRequestDelete={(id) => {
          setIdRespostaRapida(id);
          setOpenModalConfirmacao(true);
        }}
      />

      <ModalConfimacao
        open={openModalConfirmacao}
        onClose={() => setOpenModalConfirmacao(false)}
        onConfirm={handleDelete}
        loadingFinalizar={deleting}
        mensagem={t('quickReplies.deleteConfirm')}
      />
    </>
  );
}
